import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { AuthError, Session, User } from '@supabase/supabase-js';

export type { AuthError, Session, User };
export type AppRole = Database['public']['Enums']['app_role'];

export type OrgRole = 'owner' | 'admin' | 'atendente' | 'prestador';

/**
 * Decodes the JWT access token's `app_metadata` to extract org context.
 * Pure function — no I/O. Returns nulls if token is missing, malformed,
 * or lacks `app_metadata.org_id`/`org_role` claims.
 */
export function extractOrgContext(session: Session | null): { orgId: string | null; orgRole: OrgRole | null } {
  if (!session?.access_token) return { orgId: null, orgRole: null };

  try {
    const payload = JSON.parse(atob(session.access_token.split('.')[1]));
    const appMetadata = payload.app_metadata || {};
    return {
      orgId: appMetadata.org_id || null,
      orgRole: appMetadata.org_role || null,
    };
  } catch {
    return { orgId: null, orgRole: null };
  }
}

/**
 * Fetches the current session from Supabase Auth.
 * Returns null if no session is active.
 */
export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Subscribes to Supabase auth state changes (login, logout, token refresh, etc).
 * Returns an object with an `unsubscribe` function to be called on cleanup.
 */
export function subscribeToAuthChanges(
  onChange: (event: string, session: Session | null) => void,
): { unsubscribe: () => void } {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(onChange);
  return { unsubscribe: () => subscription.unsubscribe() };
}

/**
 * Signs in a user with email and password.
 * Returns Supabase's raw `{ error }` result for consumer handling.
 */
export async function signInWithPassword(params: {
  email: string;
  password: string;
}): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signInWithPassword({
    email: params.email,
    password: params.password,
  });
  return { error };
}

/**
 * Signs up a new user with email, password, and display name.
 * `redirectUrl` is passed to Supabase as `emailRedirectTo` for confirmation emails.
 * Returns `{ error, needsEmailConfirmation }`. The confirmation flag is true when
 * signup succeeded but no session was returned (Supabase requires email verification).
 */
export async function signUp(params: {
  email: string;
  password: string;
  nome: string;
  redirectUrl: string;
}): Promise<{ error: AuthError | null; needsEmailConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email: params.email,
    password: params.password,
    options: {
      emailRedirectTo: params.redirectUrl,
      data: { nome: params.nome },
    },
  });
  return { error, needsEmailConfirmation: !error && !data.session };
}

/**
 * Signs out the current user.
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signOut();
  return { error };
}
